// src/pages/Settings.jsx
import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, CreditCard, ArrowLeft, Save, Plus, Edit2, Upload,
  CheckCircle, XCircle, AlertTriangle, Bell
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { 
  updateUser, 
  getStates, 
  getLgasByState, 
  getLcdasByLga, 
  getPayoutMethods, 
  BASE_URL 
} from '../../services/api';
import Loading from '../../components/common/Loading';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import ProfileTab from '../../components/settings/ProfileTab';
import PaymentTab from '../../components/settings/PaymentTab';
import NotificationSettings from '../../components/notifications/NotificationSettings';
import { getImageUrl } from '../../utils/imageUtils';

const Settings = () => {
  const { user, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // State
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('settings_active_tab') || 'profile';
  });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    middle_name: '',
    email: '',
    phone_number: '',
    state_id: '',
    lga_id: '',
    lcda_id: '',
  });
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [lcdas, setLcdas] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [payoutMethods, setPayoutMethods] = useState([]);
  const [loadingPayoutMethods, setLoadingPayoutMethods] = useState(false);

  // Effects
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        middle_name: user.middle_name || '',
        email: user.email || '',
        phone_number: user.phone_number || '',
        state_id: user.state_id || '',
        lga_id: user.lga_id || '',
        lcda_id: user.lcda_id || '',
      });
      
      loadStates();
    }
  }, [user]);

  useEffect(() => {
    if (formData.state_id) {
      loadLgas(formData.state_id);
    }
  }, [formData.state_id]);

  useEffect(() => {
    if (formData.lga_id) {
      loadLcdas(formData.lga_id);
    }
  }, [formData.lga_id]);

  useEffect(() => {
    if (activeTab === 'payment') {
      fetchPayoutMethods();
    }
  }, [activeTab]);

  // API Calls
  const loadStates = useCallback(async () => {
    try {
      const response = await getStates();
      if (response.data) {
        setStates(response.data);
      }
    } catch (err) {
      // console.error('Error loading states:', err);
    }
  }, []);

  const loadLgas = useCallback(async (stateId) => {
    try {
      const response = await getLgasByState(stateId);
      if (response.data) {
        setLgas(response.data);
      }
    } catch (err) {
      // console.error('Error loading LGAs:', err);
      setLgas([]);
    }
  }, []);

  const loadLcdas = useCallback(async (lgaId) => {
    try {
      const response = await getLcdasByLga(lgaId);
      if (response.data) {
        setLcdas(response.data);
      }
    } catch (err) {
      // console.error('Error loading LCDAs:', err);
      setLcdas([]);
    }
  }, []);

  const fetchPayoutMethods = useCallback(async () => {
    if (!user?.user_id) return;
    
    try {
      setLoadingPayoutMethods(true);
      const response = await getPayoutMethods(user.user_id);
      
      if (response.success && response.payout_methods) {
        setPayoutMethods(response.payout_methods);
      }
    } catch (err) {
      // console.error('Error fetching payout methods:', err);
      setErrorMessage('Failed to load payout methods');
    } finally {
      setLoadingPayoutMethods(false);
    }
  }, [user?.user_id]);

  // Event Handlers
  const handleInputChange = useCallback((name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleProfilePictureChange = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setErrorMessage('Please select a valid image (JPEG, PNG, GIF, WebP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('Image size should be less than 5MB');
      return;
    }

    try {
      setUploadingPhoto(true);
      setErrorMessage('');
      setSuccessMessage('');

      const formData = new FormData();
      formData.append('user_id', user.user_id);
      formData.append('image', file);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/upload-profile-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        setSuccessMessage('Profile picture updated successfully!');
        
        if (updateUserProfile && result.data) {
          const newImageUrl = result.data.profile_picture_url || result.data.image_url;
          if (newImageUrl) {
            updateUserProfile({ profile_picture_url: newImageUrl });
          }
        }
      } else {
        throw new Error(result.error || result.message || 'Failed to upload image');
      }
    } catch (err) {
      // console.error('Upload error:', err);
      setErrorMessage(err.message || 'Failed to upload profile picture');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  }, [user, updateUserProfile]);

  const handleSaveProfile = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      setSuccessMessage('');
      
      const response = await updateUser(user.user_id, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        middle_name: formData.middle_name,
        phone_number: formData.phone_number,
        state_id: formData.state_id,
        lga_id: formData.lga_id,
        lcda_id: formData.lcda_id,
      });
      
      if (response.success) {
        setSuccessMessage('Profile updated successfully!');
        
        if (updateUserProfile) {
          updateUserProfile({
            first_name: formData.first_name,
            last_name: formData.last_name,
            middle_name: formData.middle_name,
            phone_number: formData.phone_number,
            state_id: formData.state_id,
            lga_id: formData.lga_id,
            lcda_id: formData.lcda_id,
          });
        }
        
        setTimeout(() => {
          navigate('/profile');
        }, 1500);
      } else {
        setErrorMessage(response.error || 'Failed to update profile');
      }
    } catch (err) {
      // console.error('Error updating profile:', err);
      setErrorMessage('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [formData, user, updateUserProfile, navigate]);

  const handleNavigateToPayoutMethods = useCallback(() => {
    navigate('/wallet/payout-methods');
  }, [navigate]);

  const handleSetDefaultMethod = useCallback(async (methodId) => {
    try {
      // API call to set default method
      fetchPayoutMethods(); // Refresh list
      setSuccessMessage('Default payout method updated');
    } catch (err) {
      // console.error('Error setting default method:', err);
      setErrorMessage('Failed to update default method');
    }
  }, [fetchPayoutMethods]);

  const handleRemoveMethod = useCallback(async (methodId) => {
    try {
      // API call to remove method
      fetchPayoutMethods(); // Refresh list
      setSuccessMessage('Payout method removed');
    } catch (err) {
      // console.error('Error removing method:', err);
      setErrorMessage('Failed to remove payout method');
    }
  }, [fetchPayoutMethods]);

  const clearMessages = useCallback(() => {
    setSuccessMessage('');
    setErrorMessage('');
  }, []);

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    localStorage.setItem('settings_active_tab', tabId);
    clearMessages();
  }, [clearMessages]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <>
      {/* Mobile View */}
      <div className="lg:hidden min-h-screen bg-gray-50">
        <MobileHeader onBack={() => navigate(-1)} />
        
        <Messages 
          successMessage={successMessage} 
          errorMessage={errorMessage} 
        />
        
        <TabNavigation 
          activeTab={activeTab} 
          onTabChange={handleTabChange} 
        />
        
        <div className="px-4 pb-20">
          {activeTab === 'profile' && (
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              <ProfileTab
                user={user}
                formData={formData}
                states={states}
                lgas={lgas}
                lcdas={lcdas}
                loading={loading}
                uploadingPhoto={uploadingPhoto}
                onInputChange={handleInputChange}
                onSaveProfile={handleSaveProfile}
                onProfilePictureChange={handleProfilePictureChange}
              />
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              <h2 className="text-lg font-semibold mb-4">Notification Preferences</h2>
              <NotificationSettings />
            </div>
          )}

          {activeTab === 'payment' && (
            <PaymentTab
              payoutMethods={payoutMethods}
              loadingPayoutMethods={loadingPayoutMethods}
              onNavigateToPayoutMethods={handleNavigateToPayoutMethods}
              onSetDefaultMethod={handleSetDefaultMethod}
              onRemoveMethod={handleRemoveMethod}
            />
          )}
        </div>

        <MobileBottomNav />
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block min-h-screen bg-gray-50">
        <Header />
        
        <DesktopMain 
          activeTab={activeTab}
          onTabChange={handleTabChange}
          successMessage={successMessage}
          errorMessage={errorMessage}
          user={user}
          formData={formData}
          states={states}
          lgas={lgas}
          lcdas={lcdas}
          loading={loading}
          uploadingPhoto={uploadingPhoto}
          payoutMethods={payoutMethods}
          loadingPayoutMethods={loadingPayoutMethods}
          onInputChange={handleInputChange}
          onSaveProfile={handleSaveProfile}
          onProfilePictureChange={handleProfilePictureChange}
          onNavigateToPayoutMethods={handleNavigateToPayoutMethods}
          onSetDefaultMethod={handleSetDefaultMethod}
          onRemoveMethod={handleRemoveMethod}
          fileInputRef={fileInputRef}
          navigate={navigate}
        />
        
        <Footer />
      </div>
    </>
  );
};

// Static Components
const MobileHeader = memo(({ onBack }) => (
  <header className="bg-white shadow-sm sticky top-0 z-50">
    <div className="px-4 py-3">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="w-5 h-5 text-black" />
        </button>
        
        <h1 className="text-lg font-bold text-black">Settings</h1>
        
        <div className="w-10"></div>
      </div>
    </div>
  </header>
));

const Messages = memo(({ successMessage, errorMessage }) => (
  <div className="px-4 py-3">
    {successMessage && (
      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
        <div className="flex items-center">
          <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
          <p className="text-green-700 text-sm">{successMessage}</p>
        </div>
      </div>
    )}

    {errorMessage && (
      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
        <div className="flex items-center">
          <XCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
          <p className="text-red-700 text-sm">{errorMessage}</p>
        </div>
      </div>
    )}
  </div>
));

const TabNavigation = memo(({ activeTab, onTabChange }) => (
  <div className="px-4 mb-4">
    <div className="flex overflow-x-auto space-x-2 pb-2">
      {[
        { id: 'profile', label: 'Profile', icon: '👤' },
        { id: 'notifications', label: 'Notifications', icon: '🔔' },
        { id: 'payment', label: 'Payment', icon: '💳' }
      ].map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex items-center gap-1 ${
            activeTab === tab.id
              ? 'bg-[#ff660e] text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <span>{tab.icon}</span>
          {tab.label}
        </button>
      ))}
    </div>
  </div>
));

const DesktopMain = memo(({
  activeTab,
  onTabChange,
  successMessage,
  errorMessage,
  user,
  formData,
  states,
  lgas,
  lcdas,
  loading,
  uploadingPhoto,
  payoutMethods,
  loadingPayoutMethods,
  onInputChange,
  onSaveProfile,
  onProfilePictureChange,
  onNavigateToPayoutMethods,
  onSetDefaultMethod,
  onRemoveMethod,
  fileInputRef,
  navigate
}) => (
  <main className="max-w-7xl mx-auto px-6 py-8">
    <div className="flex items-center justify-between mb-8">
      <div>
        <h1 className="text-3xl font-bold text-black">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account preferences</p>
      </div>
    </div>

    {(successMessage || errorMessage) && (
      <div className="mb-6">
        {successMessage && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <p className="text-green-700">{successMessage}</p>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-red-700">{errorMessage}</p>
            </div>
          </div>
        )}
      </div>
    )}

    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Left Column - Navigation */}
      <div className="lg:col-span-1">
        <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
          <nav className="space-y-1">
            <button
              onClick={() => onTabChange('profile')}
              className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                activeTab === 'profile'
                  ? 'bg-[#ff660e] text-white'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              <User className={`w-5 h-5 ${activeTab === 'profile' ? 'text-white' : 'text-gray-500'}`} />
              <span className="font-medium">Profile Settings</span>
            </button>

            <button
              onClick={() => onTabChange('notifications')}
              className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                activeTab === 'notifications'
                  ? 'bg-[#ff660e] text-white'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              <Bell className={`w-5 h-5 ${activeTab === 'notifications' ? 'text-white' : 'text-gray-500'}`} />
              <span className="font-medium">Notification Settings</span>
            </button>

            <button
              onClick={() => onTabChange('payment')}
              className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                activeTab === 'payment'
                  ? 'bg-[#ff660e] text-white'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              <CreditCard className={`w-5 h-5 ${activeTab === 'payment' ? 'text-white' : 'text-gray-500'}`} />
              <span className="font-medium">Payment Methods</span>
            </button>

            <div className="pt-4 mt-4 border-t">
              <button
                onClick={() => navigate('/profile')}
                className="w-full flex items-center gap-3 p-3 rounded-lg text-left hover:bg-gray-50 text-gray-700"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
                <span className="font-medium">Back to Profile</span>
              </button>
            </div>
          </nav>
        </div>
      </div>

      {/* Right Column - Main Content */}
      <div className="lg:col-span-3 space-y-6">
        {activeTab === 'profile' && (
          <DesktopProfileTab
            user={user}
            formData={formData}
            states={states}
            lgas={lgas}
            lcdas={lcdas}
            loading={loading}
            uploadingPhoto={uploadingPhoto}
            onInputChange={onInputChange}
            onSaveProfile={onSaveProfile}
            onProfilePictureChange={onProfilePictureChange}
            fileInputRef={fileInputRef}
          />
        )}

        {activeTab === 'notifications' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-black mb-6">Notification Preferences</h2>
            <NotificationSettings />
          </div>
        )}

        {activeTab === 'payment' && (
          <DesktopPaymentTab
            payoutMethods={payoutMethods}
            loadingPayoutMethods={loadingPayoutMethods}
            onNavigateToPayoutMethods={onNavigateToPayoutMethods}
            onSetDefaultMethod={onSetDefaultMethod}
            onRemoveMethod={onRemoveMethod}
          />
        )}
      </div>
    </div>
  </main>
));

const DesktopProfileTab = memo(({
  user,
  formData,
  states,
  lgas,
  lcdas,
  loading,
  uploadingPhoto,
  onInputChange,
  onSaveProfile,
  onProfilePictureChange,
  fileInputRef
}) => {
  const [focusedField, setFocusedField] = useState(null);

  const handleFocus = useCallback((fieldName) => {
    setFocusedField(fieldName);
  }, []);

  const handleBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  const handleProfilePictureClick = useCallback(() => {
    fileInputRef.current.click();
  }, [fileInputRef]);

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-black">Profile Settings</h2>
        <button
          onClick={onSaveProfile}
          disabled={loading || uploadingPhoto}
          className="px-6 py-3 bg-[#ff660e] text-white rounded-lg font-semibold hover:bg-[#e55a0d] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </button>
      </div>

      <div className="flex items-center gap-6 mb-8 pb-8 border-b">
        <div className="relative">
          <div className="w-24 h-24 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gradient-to-br from-[#ff660e] to-orange-500">
            {user?.profile_picture_url ? (
              <img 
                src={getImageUrl(user.profile_picture_url)}
                alt={`${user.first_name} ${user.last_name}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-12 h-12 text-white" />
              </div>
            )}
          </div>
          {uploadingPhoto && (
            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onProfilePictureChange}
          />
          
          <button 
            onClick={handleProfilePictureClick}
            disabled={uploadingPhoto}
            className="absolute -bottom-2 -right-2 bg-white p-2 rounded-full shadow-lg border"
          >
            {uploadingPhoto ? (
              <div className="w-4 h-4 border-2 border-[#ff660e] border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Edit2 className="w-4 h-4 text-[#ff660e]" />
            )}
          </button>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-black mb-1">
            {user?.first_name} {user?.middle_name} {user?.last_name}
          </h3>
          <p className="text-gray-600 mb-3">{user?.email}</p>
          <button
            onClick={handleProfilePictureClick}
            disabled={uploadingPhoto}
            className="text-sm text-[#ff660e] font-medium hover:text-[#e55a0d] transition-colors disabled:opacity-50 flex items-center"
          >
            <Upload className="w-4 h-4 mr-1" />
            {uploadingPhoto ? 'Uploading...' : 'Change Profile Picture'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
          <input
            type="text"
            value={formData.first_name}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50"
          />
          <p className="text-xs text-gray-500 mt-2">Contact support to change first name</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
          <input
            type="text"
            value={formData.last_name}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50"
          />
          <p className="text-xs text-gray-500 mt-2">Contact support to change last name</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Middle Name</label>
          <input
            type="text"
            value={formData.middle_name}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50"
          />
          <p className="text-xs text-gray-500 mt-2">Contact support to change middle name</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
          <input
            type="email"
            value={formData.email}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50"
          />
          <p className="text-xs text-gray-500 mt-2">Contact support to change email</p>
        </div>

        <DesktopFormField
          label="Phone Number *"
          name="phone_number"
          value={formData.phone_number}
          onChange={(e) => onInputChange('phone_number', e.target.value)}
          focusedField={focusedField}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="+234 800 000 0000"
          required
        />

        <DesktopSelectField
          label="State *"
          name="state_id"
          value={formData.state_id}
          onChange={(e) => onInputChange('state_id', e.target.value)}
          focusedField={focusedField}
          onFocus={handleFocus}
          onBlur={handleBlur}
          options={states}
          optionKey="state_id"
          optionLabel="name"
          placeholder="Select State"
          required
        />

        <DesktopSelectField
          label="Local Government Area (LGA) *"
          name="lga_id"
          value={formData.lga_id}
          onChange={(e) => onInputChange('lga_id', e.target.value)}
          focusedField={focusedField}
          onFocus={handleFocus}
          onBlur={handleBlur}
          options={lgas}
          optionKey="lga_id"
          optionLabel="name"
          placeholder="Select LGA"
          disabled={!formData.state_id}
          required
        />

        <DesktopSelectField
          label="Local Council Development Area (LCDA)"
          name="lcda_id"
          value={formData.lcda_id}
          onChange={(e) => onInputChange('lcda_id', e.target.value)}
          focusedField={focusedField}
          onFocus={handleFocus}
          onBlur={handleBlur}
          options={lcdas}
          optionKey="lcda_id"
          optionLabel="name"
          placeholder="Select LCDA (Optional)"
          disabled={!formData.lga_id}
        />
      </div>
    </div>
  );
});

const DesktopPaymentTab = memo(({
  payoutMethods,
  loadingPayoutMethods,
  onNavigateToPayoutMethods,
  onSetDefaultMethod,
  onRemoveMethod
}) => {
  const parsePayoutDetails = useCallback((details) => {
    try {
      return JSON.parse(details);
    } catch (err) {
      return {};
    }
  }, []);

  const getBankIcon = useCallback((bankName) => {
    const bankIcons = {
      'OPay': '🟢',
      'GTBank': '🟣',
      'First Bank': '🔵',
      'Access Bank': '🔴',
      'Zenith Bank': '🟠',
      'UBA': '🟡',
    };

    for (const [key, icon] of Object.entries(bankIcons)) {
      if (bankName.toLowerCase().includes(key.toLowerCase())) {
        return icon;
      }
    }
    return '🏦';
  }, []);

  const handleSetDefault = useCallback((methodId) => {
    onSetDefaultMethod(methodId);
  }, [onSetDefaultMethod]);

  const handleRemove = useCallback((methodId) => {
    if (window.confirm('Are you sure you want to remove this payout method?')) {
      onRemoveMethod(methodId);
    }
  }, [onRemoveMethod]);

  if (loadingPayoutMethods) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="text-center py-12">
          <div className="w-10 h-10 border-2 border-[#ff660e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment methods...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-black mb-1">Payment Methods</h2>
          <p className="text-gray-600">Manage your payout accounts</p>
        </div>
        <button 
          onClick={onNavigateToPayoutMethods}
          className="px-6 py-3 bg-[#ff660e] text-white rounded-lg font-semibold hover:bg-[#e55a0d] transition-all flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Method
        </button>
      </div>

      {payoutMethods.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {payoutMethods.map((method) => {
            const details = parsePayoutDetails(method.details);
            const verification = method.verification_status || {};
            
            return (
              <DesktopPaymentMethodCard
                key={method.payout_method_id}
                method={method}
                details={details}
                verification={verification}
                getBankIcon={getBankIcon}
                onSetDefault={handleSetDefault}
                onRemove={handleRemove}
              />
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CreditCard className="w-10 h-10 text-gray-400" />
          </div>
          <h4 className="text-lg font-medium mb-3">No payment methods added</h4>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Add a payment method to receive payments for your sales and services
          </p>
          <button 
            onClick={onNavigateToPayoutMethods}
            className="px-8 py-3.5 bg-[#ff660e] text-white rounded-lg font-semibold hover:bg-[#e55a0d] transition-all"
          >
            Add Your First Payment Method
          </button>
        </div>
      )}
    </div>
  );
});

const DesktopPaymentMethodCard = memo(({ 
  method, 
  details, 
  verification, 
  getBankIcon, 
  onSetDefault, 
  onRemove 
}) => (
  <div className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
    <div className="flex items-start gap-4 mb-4">
      <div className="w-12 h-12 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
        <span className="text-2xl">{getBankIcon(details.bank_name || 'Bank')}</span>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <h4 className="font-semibold text-black">{details.bank_name || 'Bank Account'}</h4>
          {method.is_default === 1 && (
            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
              Default
            </span>
          )}
          {method.is_verified === 1 ? (
            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
              Verified
            </span>
          ) : (
            <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
              Pending
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600">
          Account: {details.account_number || 'N/A'}
        </p>
        <p className="text-sm text-gray-600">
          Name: {details.account_name || 'N/A'}
        </p>
      </div>
    </div>

    {verification.matches_profile !== undefined && (
      <div className={`p-3 rounded-lg mb-4 text-sm ${
        verification.matches_profile 
          ? 'bg-green-50 text-green-700' 
          : 'bg-yellow-50 text-yellow-700'
      }`}>
        <div className="flex items-start">
          {verification.matches_profile ? (
            <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          ) : (
            <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          )}
          <div>
            <div className="font-medium">Account Verification</div>
            <div className="mt-1">
              {verification.matches_profile 
                ? '✓ Account name matches your profile'
                : `⚠️ Account name doesn't match profile: ${verification.user_name || 'N/A'}`}
            </div>
          </div>
        </div>
      </div>
    )}

    <div className="flex gap-3 pt-4 border-t">
      {method.is_default !== 1 && (
        <button
          onClick={() => onSetDefault(method.payout_method_id)}
          className="flex-1 py-2.5 text-sm text-[#ff660e] font-medium border border-[#ff660e] rounded-lg hover:bg-orange-50 transition-colors"
        >
          Set as Default
        </button>
      )}
      <button
        onClick={() => onRemove(method.payout_method_id)}
        className="flex-1 py-2.5 text-sm text-red-600 font-medium border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
      >
        Remove
      </button>
    </div>
  </div>
));

const DesktopFormField = memo(({ 
  label, 
  name, 
  value, 
  onChange, 
  readOnly = false,
  focusedField, 
  onFocus, 
  onBlur, 
  placeholder, 
  type = 'text',
  required 
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      onFocus={() => onFocus(name)}
      onBlur={onBlur}
      className={`w-full px-4 py-3 border border-gray-300 rounded-xl transition-all ${
        focusedField === name 
          ? 'border-[#ff660e] ring-2 ring-[#ff660e] ring-opacity-50' 
          : 'focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent'
      } ${readOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
      readOnly={readOnly}
      placeholder={placeholder}
      required={required}
    />
    {readOnly && (
      <p className="text-xs text-gray-500 mt-2">
        Contact support to change {label.toLowerCase().replace(' *', '')}
      </p>
    )}
  </div>
));

const DesktopSelectField = memo(({ 
  label, 
  name, 
  value, 
  onChange, 
  focusedField, 
  onFocus, 
  onBlur, 
  options, 
  optionKey, 
  optionLabel, 
  placeholder, 
  disabled, 
  required 
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      onFocus={() => onFocus(name)}
      onBlur={onBlur}
      className={`w-full px-4 py-3 border border-gray-300 rounded-xl transition-all ${
        focusedField === name 
          ? 'border-[#ff660e] ring-2 ring-[#ff660e] ring-opacity-50' 
          : 'focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      disabled={disabled}
      required={required}
    >
      <option value="">{placeholder}</option>
      {options.map(option => (
        <option key={option[optionKey]} value={option[optionKey]}>
          {option[optionLabel]}
        </option>
      ))}
    </select>
  </div>
));

export default Settings;